import { useAuthContext } from '@/components/Auth/AuthContext';
import ErrorMap from '@/components/Auth/ErrorMap';
import Button from '@/components/Button';
import Form from '@/components/Form/Form';
import TextInput from '@/components/Form/TextInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import SimpleLink from '@/components/SimpleLink';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import * as Auth from 'aws-amplify/auth';
import { Redirect, router } from 'expo-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

type LoginForm = {
  email: string;
  password: string;
};

type ResetPasswordForm = {
  motDePasse: string;
  motDePasseConfirmation: string;
  code: string;
};

const LoginPage = () => {
  const authInfos = useAuthContext();
  const { height } = useWindowDimensions();

  const [user, setUser] = useState<any>();

  const { t } = useTranslation();

  const loginForm = useForm<LoginForm>();
  const resetPasswordForm = useForm<ResetPasswordForm>();

  const [loginIn, setLoginIn] = useState(false);

  if (authInfos.jwt) {
    return <Redirect href="/patients" />;
  }

  return (
    <View style={[styles.screen, { height }]}>
      <ScrollView
        contentContainerStyle={[styles.loginInner, { paddingHorizontal: 40 }]}
        style={{ flex: 1 }}
      >
        <SafeAreaView style={styles.logoParent}>
          {user && user.nextStep?.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE' ? (
            <Form {...resetPasswordForm}>
              <Text style={styles.title}>{t('Récupérer mot de passe')}</Text>
              <View style={styles.frameParent}>
                <View style={[styles.infosRapides, styles.marginInfosRapidesBottom]}>
                  <View style={[styles.texte, styles.texteFlexBox]}>
                    <Text style={styles.vousSouhaitezRejoindre}>
                      {t('Vous devez modifier votre mot de passe pour pouvoir vous connecter.')}
                    </Text>
                  </View>
                </View>
                <TextInput
                  name="code"
                  label="Code"
                  placeholder="Votre code reçu par email"
                  validators={[AvailableValidationRules.required]}
                />
                <TextInput
                  name={'motDePasse'}
                  label={'Nouveau mot de passe'}
                  secureTextEntry
                  validators={[
                    {
                      rule: AvailableValidationRules.required,
                      errorMessage: 'Vous devez renseigner votre nouveau mot de passe',
                      ifNotEmpty: ['motDePasse'],
                    },
                    {
                      rule: AvailableValidationRules.password,
                      errorMessage:
                        'Votre mot de passe doit contenir au minimum 8 charactères dont un chiffre, une minuscule, une majuscule et un caractère special de la liste suivante : ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + =',
                    },
                  ]}
                />
                <TextInput
                  name={'motDePasseConfirmation'}
                  label={'Confirmer le mot de passe'}
                  secureTextEntry
                  validators={[
                    {
                      rule: AvailableValidationRules.required,
                      errorMessage:
                        'Vous devez confirmer votre mot de passe pour changer de mot de passe',
                      ifNotEmpty: ['motDePasse'],
                    },
                    {
                      rule: AvailableValidationRules.equals,
                      errorMessage: 'La confirmation ne correspond pas au mot de passe',
                      equals: 'motDePasse',
                    },
                  ]}
                />
                <Button
                  loading={loginIn}
                  disabled={loginIn}
                  withShadow={false}
                  style={styles.btnForm}
                  onPress={resetPasswordForm.handleSubmit(async (data) => {
                    setLoginIn(true);
                    try {
                      const result = await Auth.confirmResetPassword({
                        username: user.username,
                        confirmationCode: data.code,
                        newPassword: data.motDePasse,
                      });
                      await Auth.signIn({
                        username: user.username,
                        password: data.motDePasse,
                      });
                      setLoginIn(false);
                    } catch (err: any) {
                      if (err.code === 'NotAuthorizedException') {
                        Alert.alert('Erreur', 'Votre ancien mot de passe est incorrect.');
                      }
                      if (err.code === 'LimitExceededException') {
                        Alert.alert(
                          'Erreur',
                          'Vous avez atteint la limite de tentatives, merci de réessayer plus tard.'
                        );
                      }
                      if (err.code === 'InvalidPasswordException') {
                        Alert.alert(
                          'Erreur',
                          "Votre nouveau mot de passe n'est pas conforme à notre politique de mot de passe."
                        );
                      }
                    }
                  })}
                >
                  {'Enregistrer'}
                </Button>
              </View>
            </Form>
          ) : (
            <Form {...loginForm}>
              <Text style={styles.title}>{t('Connexion')}</Text>
              <View style={styles.frameParent}>
                <TextInput
                  name="email"
                  label={'E-mail'}
                  placeholder={'Votre adresse e-mail'}
                  validators={[AvailableValidationRules.email, AvailableValidationRules.required]}
                />
                <TextInput
                  name="password"
                  label={'Mot de passe'}
                  placeholder={'Votre mot de passe'}
                  secureTextEntry
                  validators={[AvailableValidationRules.required]}
                />
                <SimpleLink
                  active={false}
                  style={styles.motDePasseContainer}
                  textStyle={styles.motDePasse}
                  onPress={() => {
                    router.push('/motdepasse-oublie');
                  }}
                >
                  {'Mot de passe oublié ?'}
                </SimpleLink>
                <Button
                  loading={loginIn}
                  disabled={loginIn}
                  withShadow={false}
                  onPress={loginForm.handleSubmit(async (data) => {
                    setLoginIn(true);
                    try {
                      let result;
                      const user = await Auth.signIn({
                        username: data.email,
                        password: data.password,
                      });
                      if (user?.nextStep?.signInStep === 'RESET_PASSWORD') {
                        result = await Auth.resetPassword({
                          username: data.email,
                        });
                      }
                      setLoginIn(false);
                      setUser({ ...(result || {}), username: data.email });
                    } catch (error: any) {
                      setLoginIn(false);
                      Alert.alert(t('Une erreur est survenue'), ErrorMap(error));
                    }
                  })}
                >
                  {'Connexion'}
                </Button>
                <View style={[styles.infosRapides, styles.marginInfosRapidesTop]}>
                  <View style={[styles.texte, styles.texteFlexBox]}>
                    <Text style={styles.vousSouhaitezRejoindre}>
                      {t("Vous souhaitez rejoindre l'app IOL Match ?")}
                    </Text>
                    <SimpleLink href={'signup'}>{'Créez votre compte'}</SimpleLink>
                  </View>
                </View>
                <View style={[styles.infosRapides, styles.marginInfosRapidesTop]}>
                  <View style={[styles.texte, styles.texteFlexBox]}>
                    <Text style={styles.vousSouhaitezRejoindre}>
                      {t(
                        'Vous souhaitez terminer votre inscription et avez un code de vérification ?'
                      )}
                    </Text>
                    <SimpleLink href={'signup?step=2'}>{'Finalisez votre inscription'}</SimpleLink>
                  </View>
                </View>
              </View>
            </Form>
          )}
        </SafeAreaView>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  texteFlexBox: {
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  motDePasseContainer: {
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
  },
  motDePasse: {
    fontSize: FontSize.p_size,
    lineHeight: 24,
    color: Color.black,
    textAlign: 'right',
    opacity: 0.3,
    marginBottom: 14,
    fontFamily: FontFamily.labelMedium,
    alignSelf: 'stretch',
  },
  vousSouhaitezRejoindre: {
    fontSize: FontSize.labelMedium_size,
    lineHeight: 18,
    fontWeight: '500',
    color: Color.MainNoir,
    textAlign: 'center',
    display: 'flex',
    fontFamily: FontFamily.labelMedium,
    justifyContent: 'center',
    alignItems: 'center',
  },
  texte: {
    paddingHorizontal: 0,
    paddingVertical: Padding.p_11xs,
  },
  marginInfosRapidesTop: {
    marginTop: 35,
  },
  marginInfosRapidesBottom: {
    marginBottom: 35,
  },
  infosRapides: {
    padding: Padding.p_11xl,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderRadius: Border.br_3xs,
    borderStyle: 'solid',
    alignItems: 'center',
    justifyContent: 'center',
  },
  frameParent: {
    marginTop: 50,
  },
  logoParent: {
    maxWidth: 500,
    width: '100%',
  },
  loginInner: {
    paddingVertical: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  screen: {
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    marginTop: 30,
    fontFamily: FontFamily.labelBold,
    fontSize: FontSize.h3_size,
    color: Color.MainNoir,
    textAlign: 'center',
  },
  btnForm: {
    marginTop: 20,
  },
});

export default LoginPage;
